import React, { memo, useRef } from 'react';

type ImageMode =
    | 'scaleToFill'
    | 'aspectFit'
    | 'aspectFill'
    | 'widthFix'
    | 'heightFix'
    | 'top'
    | 'bottom'
    | 'center'
    | 'left'
    | 'right'
    | 'top left'
    | 'top right'
    | 'bottom left'
    | 'bottom right';
export interface ImageProps {
    /** Image source URL */
    src: string;
    /** Image mode - controls how the image should be resized */
    mode?: ImageMode;
    /** Whether to lazy load the image */
    lazyLoad?: boolean;
    /** Whether to show the image in webp format when supported by the browser */
    webp?: boolean;
    /** Whether to show the image in original size */
    originalSize?: boolean;
    /** Whether to enable default error placeholder */
    showMenuByLongpress?: boolean;
    /** Custom class name */
    className?: string;
    /** Custom style */
    style?: React.CSSProperties;
    /** 隐藏加载错误的节点 */
    hideErrorNode?: boolean;
    /** Callback when image is loaded */
    onLoad?: (e: any) => void;
    /** Callback when image fails to load */
    onError?: (e: any) => void;
    /** Callback when image is clicked */
    onClick?: (e: any) => void;
}

const Image: React.FC<ImageProps> = ({
    src,
    mode = 'scaleToFill',
    webp = false,
    originalSize = false,
    className,
    style,
    hideErrorNode = true,
    onLoad,
    onError,
    onClick,
}) => {
    const imgContext = useRef<HTMLImageElement | null>(null);

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        onLoad?.(e);
    };

    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        hideErrorNode && imgContext.current?.setAttribute('style', 'display: none');
        onError?.(e);
    };

    const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
        onClick?.(e);
    };

    // Apply mode-specific styles
    const getModeStyle = (): React.CSSProperties => {
        switch (mode) {
            case 'scaleToFill':
                return { objectFit: 'fill' };
            case 'aspectFit':
                return { objectFit: 'contain' };
            case 'aspectFill':
                return { objectFit: 'cover' };
            case 'widthFix':
                return { width: '100%', height: 'auto' };
            case 'heightFix':
                return { width: 'auto', height: '100%' };
            case 'top':
                return { objectFit: 'cover', objectPosition: 'center top' };
            case 'bottom':
                return { objectFit: 'cover', objectPosition: 'center bottom' };
            case 'center':
                return { objectFit: 'cover', objectPosition: 'center center' };
            case 'left':
                return { objectFit: 'cover', objectPosition: 'left center' };
            case 'right':
                return { objectFit: 'cover', objectPosition: 'right center' };
            case 'top left':
                return { objectFit: 'cover', objectPosition: 'left top' };
            case 'top right':
                return { objectFit: 'cover', objectPosition: 'right top' };
            case 'bottom left':
                return { objectFit: 'cover', objectPosition: 'left bottom' };
            case 'bottom right':
                return { objectFit: 'cover', objectPosition: 'right bottom' };
            default:
                return { objectFit: 'fill' };
        }
    };

    // Combine all styles
    const combinedStyle: React.CSSProperties = {
        ...getModeStyle(),
        ...style,
    };

    // If originalSize is true, remove any width/height constraints
    if (originalSize) {
        delete combinedStyle.width;
        delete combinedStyle.height;
    }

    // Determine image source with webp support if needed
    const imageSrc = webp ? `${src}?x-oss-process=image/format,webp` : src;

    return (
        <img
            ref={imgContext}
            src={imageSrc}
            className={className}
            style={combinedStyle}
            onLoad={handleLoad}
            onError={handleError}
            onClick={handleClick}
        />
    );
};

export default memo(Image);
